import { BASE_SERVICE_URI } from '../../constants/ApiConstants';
import { dataFetchingClient, registerMutation, registerQuery, useMutation, useQuery } from '../../utils/DataFetchingClient';
import { http } from '../http';
import { useCallback } from 'react';
import Raven from 'raven-js';
import { getQueryFieldName } from '../../utils/DataFetchingClientCache';
import { TABLE_SIZE } from '../table';
export const ASSIGNMENTS_PROGRESS_API = `${BASE_SERVICE_URI}/progress/v1/team/playlist-assignments/`;
export const ASSIGNMENTS_API = `${BASE_SERVICE_URI}/assignments/v1/playlist/`;
const GET_PLAYLIST_ASSIGNMENT_PORTALS = registerQuery({
  fieldName: 'playlistAssignmentsPortals',
  args: ['playlistId', 'offset', 'count'],
  fetcher: ({
    playlistId,
    offset,
    count
  }) => http.get(`${ASSIGNMENTS_API}dummy-aggregation/${playlistId}`, {
    offset,
    count
  })
});
export const usePlaylistAssignmentsPortals = ({
  playlistId,
  offset = 0,
  count = TABLE_SIZE,
  skip = false
}) => {
  const {
    data,
    loading,
    error
  } = useQuery(GET_PLAYLIST_ASSIGNMENT_PORTALS, {
    skip,
    variables: {
      playlistId,
      offset,
      count
    }
  });
  return {
    total: data ? data.total : 0,
    paging: data ? data.paging : null,
    playlistAssignmentsPortals: data ? data.results : null,
    playlistAssignmentsPortalsLoading: loading,
    playlistAssignmentsPortalsError: error
  };
};
const GET_PLAYLIST_ASSIGNMENTS_FOR_PORTAL = registerQuery({
  fieldName: 'playlistAssignments',
  args: ['playlistId', 'offset', 'count', 'portalId'],
  fetcher: ({
    playlistId,
    offset,
    count,
    portalId
  }) => http.get(`${ASSIGNMENTS_API}/dummy/${playlistId}/${portalId}`, {
    offset,
    count
  })
});
export const usePlaylistAssignmentsForPortal = ({
  playlistId,
  portalId,
  offset,
  count = TABLE_SIZE,
  skip = false
}) => {
  const {
    data,
    loading,
    error
  } = useQuery(GET_PLAYLIST_ASSIGNMENTS_FOR_PORTAL, {
    skip,
    variables: {
      playlistId,
      portalId,
      offset,
      count
    }
  });
  return {
    total: data ? data.total : 0,
    paging: data ? data.paging : null,
    playlistAssignmentsForPortal: data ? data.results : null,
    playlistAssignmentsForPortalLoading: loading,
    playlistAssignmentsForPortalError: error
  };
};

const refetchAssignments = (playlistId, portalId) => dataFetchingClient.refetchQueries({
  updateCache(cache) {
    cache.evict({
      fieldName: getQueryFieldName(GET_PLAYLIST_ASSIGNMENTS_FOR_PORTAL),
      args: {
        playlistId,
        portalId
      }
    });
  }

});

const CREATE_PLAYLIST_ASSIGNMENT = registerMutation({
  fieldName: 'createPlaylistAssignment',
  args: ['playlistId', 'portalId', 'assigneeUserIds', 'dueDate'],
  fetcher: data => http.post(ASSIGNMENTS_API, {
    data
  })
});
export const useCreatePlaylistAssignment = ({
  onCompleted
} = {}) => {
  const [mutate, {
    loading: createPlaylistAssignmentLoading,
    error: createPlaylistAssignmentError
  }] = useMutation(CREATE_PLAYLIST_ASSIGNMENT, {
    update: async (cache, {
      data,
      errors
    }, {
      variables
    }) => {
      if (!data || !variables || errors && errors.length) return; // refetches the list of assignments for this portal.

      const {
        playlistId,
        portalId
      } = variables;
      await refetchAssignments(playlistId, portalId);
    },
    onCompleted: assignmentsRecord => {
      if (onCompleted) onCompleted(assignmentsRecord.createPlaylistAssignment);
    }
  });
  const createPlaylistAssignment = useCallback(variables => {
    return mutate({
      variables
    }).catch(error => {
      Raven.captureException(error);
    });
  }, [mutate]);
  return {
    createPlaylistAssignment,
    createPlaylistAssignmentLoading,
    createPlaylistAssignmentError
  };
};
const UPDATE_PLAYLIST_ASSIGNMENT = registerMutation({
  fieldName: 'updatePlaylistAssignment',
  args: ['assignmentId', 'dueDate'],
  fetcher: ({
    assignmentId,
    dueDate
  }) => http.patch(`${ASSIGNMENTS_API}/${assignmentId}`, {
    data: {
      dueDate
    }
  })
});
export const useUpdatePlaylistAssignment = ({
  onCompleted
} = {}) => {
  const [mutate, {
    loading: updatePlaylistAssignmentLoading,
    error: updatePlaylistAssignmentError
  }] = useMutation(UPDATE_PLAYLIST_ASSIGNMENT, {
    update: async (cache, {
      data,
      errors
    }, {
      variables
    }) => {
      if (!data || !variables || errors && errors.length) return; // refetches the list of assignments for this portal.

      const {
        playlistId,
        portalId
      } = data.updatePlaylistAssignment;
      await refetchAssignments(playlistId, portalId);
    },
    onCompleted: assignmentsRecord => {
      if (onCompleted) onCompleted(assignmentsRecord.updatePlaylistAssignment);
    }
  });
  const updatePlaylistAssignment = useCallback(variables => {
    return mutate({
      variables
    }).catch(error => {
      Raven.captureException(error);
    });
  }, [mutate]);
  return {
    updatePlaylistAssignment,
    updatePlaylistAssignmentLoading,
    updatePlaylistAssignmentError
  };
};
const DELETE_PLAYLIST_ASSIGNMENT = registerMutation({
  fieldName: 'deletePlaylistAssignment',
  args: ['assignmentId'],
  fetcher: ({
    assignmentId
  }) => http.delete(`${ASSIGNMENTS_API}/${assignmentId}`)
});
export const useDeletePlaylistAssignment = ({
  onCompleted
} = {}) => {
  const [mutate, {
    loading: deletePlaylistAssignmentLoading,
    error: deletePlaylistAssignmentError
  }] = useMutation(DELETE_PLAYLIST_ASSIGNMENT, {
    onCompleted: () => {
      if (onCompleted) onCompleted(); // TODO: refetch get assignments once Hektor updates endpoint to return deleted item info
    }
  });
  const deletePlaylistAssignment = useCallback(variables => {
    return mutate({
      variables
    }).catch(error => {
      Raven.captureException(error);
    });
  }, [mutate]);
  return {
    deletePlaylistAssignment,
    deletePlaylistAssignmentLoading,
    deletePlaylistAssignmentError
  };
};